import React, { useContext, useState, useMemo, useEffect } from 'react';

// import PerfectScrollbar from 'react-perfect-scrollbar';
// import 'react-perfect-scrollbar/dist/css/styles.css';

import { Badge } from 'antd';

import { getNow, showDateNoTime } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';

import { useRemoveLegal } from '@hooks/API/Contact/UseContactLegals';

import CountryLabel from '@components/Gui/CountryLabel';
import { NwAddButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';
import LegalForm from '@components/Legal/LegalForm';

import { Wrapper, StyledTag, LegalDetails } from './LegalItemUI';
import LegalUpload from './LegalUpload';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const YearlyLegalItem = ({ documents, contactId, readOnly, onEditDoc }) => {
    const currentYear = getNow().year();
    let defaultYear = documents.Years.find((el) => el === currentYear);
    if (!defaultYear) {
        defaultYear = documents.Years.sort((a, b) => a - b).reverse()[0];
    }
    const [selectedYear, setSelectedYear] = useState(defaultYear);
    const [visibleDocuments, setVisibleDocuments] = useState([]);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);

    useEffect(() => {
        setVisibleDocuments(documents.Instances.filter((el) => el.YearRef === selectedYear));
    }, [documents, selectedYear]);

    const { mutateAsync: removeLegal } = useRemoveLegal();
    const { breakpoints } = useLayoutContext();
    const { cachedList } = useContext(ListContext);
    const legalTypes = cachedList.legalTypes.items;
    const documentName = legalTypes.find((el) => el.Code.toLowerCase() === documents.Type.toLowerCase()).Name;

    const [touchForm, setTouchForm] = useState(false);

    const deleteDocument = (id) => {
        removeLegal({ LegalID: id, ContactID: contactId });
    };

    const invalid = !documents.Years.includes(currentYear);

    const LegalDocumentTitle = () => {
        return (
            <>
                {documents.Years.includes(currentYear) ? (
                    <NwIcon icon={byPrefixAndName.fasl['file-circle-check']} />
                ) : (
                    <NwIcon icon={byPrefixAndName.fasl['file-circle-xmark']} />
                )}
            </>
        );
    };

    const getCardRibbonColor = () => {
        if (documents.Years.includes(currentYear)) {
            return StyleVariables.PrimaryColor;
        }
        return StyleVariables.DangerColor;
    };

    const tabYears = useMemo(() => {
        //sort years in descending order
        const startYear = getNow().year() + 1;
        const years = [];
        let endYear = getNow().year();
        if (documents.Years && documents.Years.length > 0) {
            endYear = documents.Years.sort((a, b) => a - b)[0];
        }

        for (let i = startYear; i >= endYear; i--) {
            years.push({
                label: i,
                selected: i === selectedYear,
                valid: documents.Years.includes(i),
            });
        }
        return years;
    }, [documents, selectedYear]);

    const renderCard = () => (
        <NwCard
            title={
                <div className="legal-card-title-wrapper">
                    {breakpoints === 1 && <StyledTag color={getCardRibbonColor()}>{LegalDocumentTitle()}</StyledTag>}
                    <h1 className={`legal-card-title ${invalid ? ' invalid' : ''}`}>{documentName}</h1>
                </div>
            }
        >
            {/* <PerfectScrollbar> */}
            <div className="legal-years-selector">
                {tabYears.map((el) => (
                    <div
                        className={`legal-year-selector-item ${el.selected ? 'selected' : ''} ${el.valid ? 'valid' : 'invalid'}`}
                        onClick={() => setSelectedYear(el.label)}
                    >
                        {el.label}
                    </div>
                ))}
            </div>
            {/* </PerfectScrollbar> */}
            {visibleDocuments.length === 0 ? (
                <div className="legal-details-instance">
                    <div className="legal-details-content">
                        <div className="legal-details-content-add-button-wrapper">
                            <NwAddButton primary label="Add document" onClick={() => setAddDrawerOpen(true)} />
                        </div>
                    </div>
                </div>
            ) : (
                visibleDocuments.map((document) => (
                    <div className="legal-details-instance">
                        <div className="legal-details-content">
                            <LegalDetails
                                label="Number"
                                value={document.DocNumber}
                                button={
                                    <NwDropDownMenu
                                        size="small"
                                        actions={[
                                            {
                                                name: '',
                                                key: 'edit',
                                                pinned: true,
                                                icon: byPrefixAndName.fasl['pen'],
                                                action: () => onEditDoc(document),
                                            },
                                            {
                                                name: 'delete',
                                                type: 'delete',
                                                key: 'delete',
                                                icon: byPrefixAndName.fasl['trash-alt'],
                                                confirmationPlacement: 'left',
                                                action: () => deleteDocument(document.ID),
                                            },
                                        ]}
                                    />
                                }
                            />
                            <LegalDetails
                                label="Country"
                                value={<CountryLabel code={document.CountryCode} flag={true} />}
                            />
                            <LegalDetails
                                label="Issued on"
                                value={document.StartDate && showDateNoTime(document.StartDate)}
                            />
                            <LegalDetails
                                label="Will expire on"
                                value={document.ExpirationDate && showDateNoTime(document.ExpirationDate)}
                            />
                            <LegalDetails
                                label="Details"
                                value={<NwEllipsis popover text={document.Details} rows={4} />}
                            />
                            {!readOnly && (
                                <>
                                    <NwDivider>Scan</NwDivider>
                                    <LegalUpload item={document} />
                                </>
                            )}
                        </div>
                    </div>
                ))
            )}
        </NwCard>
    );

    // Su mobile lo stato del documento e' gia' nel tag dentro il titolo: il ribbon sarebbe un duplicato
    return (
        <Wrapper>
            {breakpoints === 1 ? (
                renderCard()
            ) : (
                <Badge.Ribbon color={getCardRibbonColor()} placement="start" text={LegalDocumentTitle()}>
                    {renderCard()}
                </Badge.Ribbon>
            )}
            {addDrawerOpen && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    title="Add Legal Document"
                    onClose={() => setAddDrawerOpen(false)}
                >
                    <LegalForm
                        ContactID={contactId}
                        onCancel={() => setAddDrawerOpen(false)}
                        onTouchForm={setTouchForm}
                        selectedYear={selectedYear}
                        selectedType={documents.Type}
                    />
                </NwDrawer>
            )}
        </Wrapper>
    );
};

export default YearlyLegalItem;
